import {
  type ReservationListServicesFragment,
  type ReservationWorkerFragment,
  formatDurationToMinutes,
  formatReservationDate,
  useToday,
} from "@stepapp/core";
import { ReservationStatus } from "@stepapp/core/apiTypes";
import {
  Avatar,
  Block,
  Button,
  Divider,
  SvgIcon,
  Typography,
  palette,
  spacing,
  useBreakpoints,
} from "@stepapp/ui";
import { addMinutes, differenceInHours, isPast, isToday } from "date-fns";
import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet } from "react-native";
import { AppStorage } from "../../services/AppStorage";
import { useDisplayIntercom } from "../../services/Intercom";
import { CanLeaveReviewStatuses } from "../../store/types";
import ReservationNote from "../Service/ReservationNote";
import WorkerWithStatus from "./../WorkerWithStatus";

type Props = {
  canLeaveReview: boolean;
  isSubscription: boolean;
  handlePress: () => void;
  status: ReservationStatus;
  postalOrZipCode: string;
  reservationName: string;
  services?: (ReservationListServicesFragment | null)[];
  startDt: string;
  timeConsumption: string;
  orderId: string;
  workers?: (ReservationWorkerFragment | null)[];
  isCalendarScreen: boolean;
  subscriptionStartDate?: string;
  customerNote?: string;
};

export const ServiceCard = ({
  canLeaveReview,
  isSubscription,
  handlePress,
  status,
  postalOrZipCode,
  reservationName,
  services,
  startDt,
  timeConsumption,
  orderId,
  isCalendarScreen,
  subscriptionStartDate,
  customerNote,
  // TODO: check with backend
  workers: _workers,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
}: Props) => {
  const { t } = useTranslation();
  const today = useToday();
  const { navigate } = useRouter();
  const { mobileOnly } = useBreakpoints();
  const countryCode = AppStorage().get("countryCode");
  const displayIntercom = useDisplayIntercom(countryCode);

  const { date, day } = useMemo(() => {
    const durationInMinutes = timeConsumption ? formatDurationToMinutes(timeConsumption) : 0;
    const day = isToday(startDt)
      ? t("screens.serviceCard.today")
      : formatReservationDate(startDt, "EEEE", subscriptionStartDate);

    return {
      date: `${formatReservationDate(startDt, "HH:mm", subscriptionStartDate)}-${formatReservationDate(addMinutes(startDt, durationInMinutes), "HH:mm, d MMMM", subscriptionStartDate)}`,
      day,
    };
  }, [subscriptionStartDate, startDt, timeConsumption, t]);

  /**
   * TODO: check with backend
   */
  const workers = useMemo(
    () =>
      _workers?.filter((worker, index, self) => {
        const firstIndex = self.findIndex((w) => w?.id === worker?.id);
        return firstIndex === index;
      }),
    [_workers],
  );

  const filteredVariants = useMemo(() => services?.filter((elem) => elem !== null), [services]);
  const paymentFailed = status === ReservationStatus.PaymentFailed;

  const isEditable = useMemo(
    () => differenceInHours(startDt, today) > 24 && isCalendarScreen,
    [startDt, today, isCalendarScreen],
  );
  const isCancelled = status === ReservationStatus.Cancelled;
  const isCompleted = CanLeaveReviewStatuses.includes(status);
  const isPastReservation = useMemo(() => isPast(startDt), [startDt]);

  const showWorkerInfo = workers && workers?.length > 0 && isCalendarScreen;
  const showCustomerNote =
    !isCompleted && isCalendarScreen && !(isPastReservation && customerNote?.length === 0);
  const showServiceAction = (isCompleted && canLeaveReview) || paymentFailed;
  const showIntercomAction = isCompleted && canLeaveReview;
  const showCardShadow = (isCompleted && canLeaveReview && isCalendarScreen) || paymentFailed;
  const showPaymentFailedShadow = showCardShadow && paymentFailed;
  const showReviewShadow = showCardShadow && !paymentFailed;

  const navigateToDetails = useCallback(() => {
    navigate(`/calendar/${orderId}/`);
  }, [navigate, orderId]);

  const handleEditNote = useCallback(() => {
    navigate({
      pathname: `/calendar/${orderId}/edit-reservation`,
      params: { questionToEdit: "customerNote" },
    });
  }, [navigate, orderId]);

  return (
    <Block
      marginVertical={mobileOnly ? 1 : 1.5}
      style={[
        styles.serviceCardContainer,
        showPaymentFailedShadow && styles.paymentCardShadow,
        showReviewShadow && styles.reviewCardShadow,
      ]}
    >
      <Block alignCenter justifySpace row marginHorizontal={2} marginTop={1.5}>
        <Block>
          <Typography decoration="capitalize" type="secondary" variant="lightLabel">
            {day}
          </Typography>

          <Typography decoration="capitalize" type="primary" variant="paragraph">
            {date}
          </Typography>
        </Block>

        {!paymentFailed && (
          <Button
            label={isEditable ? t("common.edit") : t("screens.serviceCard.details")}
            labelType="accent"
            onPress={() => navigateToDetails()}
            type="link"
          />
        )}
      </Block>

      <Divider color={palette.secondary.grey} />

      <Block paddingBottom={1.375} paddingHorizontal={2} paddingTop={1}>
        {isCancelled && (
          <Typography type="wrong" variant="lightLabelBold">
            {t("screens.serviceCard.canceled")}
          </Typography>
        )}
        <Block alignCenter justifySpace row>
          <Typography
            decoration={isCancelled ? "lineThroughCapitalize" : "capitalize"}
            type="primary"
            variant="newH3"
            style={styles.bundleName}
          >
            {reservationName}
          </Typography>

          <Block style={isCalendarScreen ? styles.postalCode : styles.postalCodeEnded}>
            <Typography type="secondary" variant="lightLabel">
              {postalOrZipCode}
            </Typography>
          </Block>
        </Block>

        {filteredVariants && filteredVariants?.length > 0 && (
          <Block marginBottom={1}>
            {status === ReservationStatus.Pending ? (
              filteredVariants.map((item) => (
                <Block key={item?.service?.id}>
                  <Typography type="primary" variant="lightLabel">
                    {`${item?.service?.name},`}
                  </Typography>
                </Block>
              ))
            ) : (
              <Typography type="primary" variant="lightLabel">
                {`${filteredVariants[0]?.service?.name}... `}
                <Typography type="primary" variant="lightLabelBold">
                  {filteredVariants.length > 1 ? `+${filteredVariants.length - 1}` : ""}
                </Typography>
              </Typography>
            )}
          </Block>
        )}

        <Block justifySpace row marginVertical={1}>
          <Block row>
            <SvgIcon
              color={palette.primary.pinkSalmon}
              name={isSubscription ? "subscription" : "additional"}
              size={24}
            />
            <Typography style={styles.subscriptionType} type="secondary" variant="lightLabel">
              {isSubscription
                ? t("screens.serviceCard.type_subscription")
                : t("screens.serviceCard.type_oneTime")}
            </Typography>
          </Block>

          {!isCalendarScreen && (
            <Block>
              {workers?.map((worker, i) => (
                <Block
                  alignCenter
                  row
                  key={worker?.id}
                  marginBottom={workers?.length - 1 === i ? 0 : 1}
                >
                  <Typography variant="lightLabelBold">{worker?.name}</Typography>
                  <Block style={styles.historyAvatar}>
                    <Avatar size={spacing.unit * 3} uri={worker?.avatar} />
                  </Block>
                </Block>
              ))}
            </Block>
          )}
        </Block>

        {showWorkerInfo && (
          <Block marginVertical={1}>
            <WorkerWithStatus
              orderId={orderId}
              workers={workers}
              status={status}
              startDate={startDt}
            />
          </Block>
        )}

        {showCustomerNote && (
          <Block marginVertical={1}>
            <ReservationNote
              note={customerNote || ""}
              type="preview"
              onPress={isPastReservation ? undefined : handleEditNote}
              reservationId={orderId}
            />
          </Block>
        )}

        <Block flex={1} row>
          {showIntercomAction && (
            <Button
              label={t("screens.serviceConfirmation.report")}
              onPress={displayIntercom}
              type="secondaryLink"
              style={styles.intercomButton}
            />
          )}
          {showServiceAction && (
            <Button
              label={
                paymentFailed
                  ? t("screens.calendar.updatePayment")
                  : t("screens.serviceRating.leaveRating")
              }
              onPress={handlePress}
              type="primary"
              style={styles.intercomButton}
            />
          )}
        </Block>
      </Block>
    </Block>
  );
};

const styles = StyleSheet.create({
  bundleName: {
    flex: 1,
  },
  intercomButton: {
    justifyContent: "center",
    flex: 1,
  },
  historyAvatar: {
    borderColor: palette.common.mediumGray,
    borderRadius: spacing.radius * 2.5,
    borderWidth: 1,
    marginLeft: 4,
  },
  paymentCardShadow: Platform.select({
    default: {
      shadowColor: palette.common.burgundy,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.25,
      shadowRadius: 16,
    },
    web: {},
  }),
  postalCode: {
    backgroundColor: palette.common.grayBackground,
    borderRadius: spacing.radius * 2.5,
    paddingHorizontal: spacing.unit,
  },
  postalCodeEnded: {
    borderColor: palette.secondary.grey,
    borderRadius: spacing.radius * 2.5,
    borderWidth: 1,
    paddingHorizontal: spacing.unit,
    paddingVertical: 3,
  },
  reviewCardShadow: Platform.select({
    default: {
      shadowColor: palette.common.paleViolet,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 1,
      shadowRadius: 16,
    },
    web: {},
  }),
  serviceCardContainer: {
    backgroundColor: palette.common.white,
    borderColor: palette.secondary.grey,
    borderRadius: spacing.radius * 2,
    borderWidth: spacing.border,
  },
  subscriptionType: {
    marginLeft: 4,
    marginTop: spacing.unit * 0.5,
  },
});
